import React, { useState, useCallback, useEffect, memo, useMemo } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import axiosInstance from '@/lib/enhancedAxiosInstance';
import { paths } from '@/lib/apiPaths';
import { Card, CardContent } from '@/components/ui/card';
import { DatePickerWithRange } from '@/components/ui/date-range-picker';
import { format } from 'date-fns';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from '@/components/ui/pagination';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { X, Filter } from 'lucide-react';
import { debounce } from 'lodash';
import { Sheet, SheetContent, SheetTrigger, SheetOverlay } from '@/components/ui/sheet';
import { DateRange } from 'react-day-picker';
import { useReportspageTranslations } from '@/hooks/useReportspageTranslations';
import { useTheme } from '@/contexts/ThemeContext';
import { useTranslation } from 'react-i18next';
import { fieldLabel } from '@/lib/fieldLabel';
import { formatDateForDisplay, DateFormat } from '@/utils/numberFormat';
import { useOrgSettings } from '@/hooks/useOrgSettings';

// Define types
interface ChangeHistoryItem {
  id: number;
  date: string;
  username: string;
  entity_type: string;
  entity_id: number;
  action: string;
  details: string;
  payload?: any; // Optional payload with detailed change information
}

// Helper to serialize date range
const serializeDateRange = (dateRange: DateRange | undefined) => ({
  from: dateRange?.from ? format(dateRange.from, 'yyyy-MM-dd') : undefined,
  to: dateRange?.to ? format(dateRange.to, 'yyyy-MM-dd') : undefined,
});

// Format entity type for display - moved up to avoid reference error
const formatEntityType = (type: string, action: string, id: number) => {
  const key = type === 'attribute_value' ? 'attribute' : type.toLowerCase();
  const label = fieldLabel(key, 'entityLabels');
  return `${label} ${id}`;
};

// Helper: map action to color class
function getActionColor(action: string) {
  const normalized = action.toLowerCase();
  if (['create', 'created'].includes(normalized)) return 'bg-green-500 text-white';
  if (['update', 'updated'].includes(normalized)) return 'bg-blue-500 text-white';
  if (['delete', 'deleted'].includes(normalized)) return 'bg-red-500 text-white';
  if (['archive', 'archived'].includes(normalized)) return 'bg-gray-500 text-white';
  return 'bg-purple-500 text-white';
}

// Helper: format detailed changes from payload
function formatDetailedChanges(
  payload: any,
  i18n: any,
  t: (key: string, params?: any) => string
): string {
  if (!payload || !payload.changes) {
    return i18n.historyDetailedChanges.noChangesAvailable;
  }

  const changes = payload.changes;
  const changeDescriptions: string[] = [];

  // Map backend field keys to translation field keys if they differ
  const fieldAlias: Record<string, string> = {
    is_active: 'status',
    is_archived: 'is_archived',
    price_type: 'price',
  };

  const translateFieldName = (rawKey: string): string => {
    return fieldLabel(fieldAlias[rawKey] || rawKey);
  };

  Object.entries(changes).forEach(([field, change]: [string, any]) => {
    if (change && typeof change === 'object' && 'old' in change && 'new' in change) {
      let oldValue = change.old;
      let newValue = change.new;

      // Try to parse JSON strings safely
      if (typeof oldValue === 'string' && (oldValue.startsWith('[') || oldValue.startsWith('{'))) {
        try {
          oldValue = JSON.parse(oldValue);
        } catch {
          // Keep as string if parsing fails
        }
      }
      if (typeof newValue === 'string' && (newValue.startsWith('[') || newValue.startsWith('{'))) {
        try {
          newValue = JSON.parse(newValue);
        } catch {
          // Keep as string if parsing fails
        }
      }

      // Translate field label
      const fieldName = translateFieldName(field);

      // Handle different types of values
      if (Array.isArray(oldValue) || Array.isArray(newValue)) {
        // Handle arrays (like tags)
        const oldArray = Array.isArray(oldValue) ? oldValue : [];
        const newArray = Array.isArray(newValue) ? newValue : [];

        if (oldArray.length === 0 && newArray.length > 0) {
          changeDescriptions.push(
            `${fieldName}: ${i18n.historyDetailedChanges.added} ${newArray.join(', ')}`
          );
        } else if (oldArray.length > 0 && newArray.length === 0) {
          changeDescriptions.push(
            `${fieldName}: ${i18n.historyDetailedChanges.removed} ${oldArray.join(', ')}`
          );
        } else {
          changeDescriptions.push(
            `${fieldName}: ${i18n.historyDetailedChanges.changedFrom} ${oldArray.join(', ')} ${i18n.historyDetailedChanges.to} ${newArray.join(', ')}`
          );
        }
      } else if (typeof oldValue === 'string' && typeof newValue === 'string') {
        // Handle strings - truncate if too long
        const truncateString = (str: string, maxLength: number = 50) => {
          return str.length > maxLength ? str.substring(0, maxLength) + '...' : str;
        };

        if ((oldValue === '' || oldValue === null) && newValue !== '' && newValue !== null) {
          const template = i18n.historyDetailedChanges.addedValue || 'Added "{{value}}"';
          changeDescriptions.push(
            `${fieldName}: ${template.replace('{{value}}', truncateString(newValue))}`
          );
        } else if (oldValue !== '' && oldValue !== null && (newValue === '' || newValue === null)) {
          const template = i18n.historyDetailedChanges.removedValue || 'Removed "{{value}}"';
          changeDescriptions.push(
            `${fieldName}: ${template.replace('{{value}}', truncateString(oldValue))}`
          );
        } else {
          const template =
            i18n.historyDetailedChanges.changedFromTo ||
            'Changed from "{{oldValue}}" to "{{newValue}}"';
          changeDescriptions.push(
            `${fieldName}: ${template.replace('{{oldValue}}', truncateString(oldValue)).replace('{{newValue}}', truncateString(newValue))}`
          );
        }
      } else {
        // Handle other types (numbers, booleans, etc.) and null values
        if (oldValue === null || oldValue === undefined) {
          changeDescriptions.push(`${fieldName}: ${i18n.historyDetailedChanges.setTo} ${newValue}`);
        } else if (newValue === null || newValue === undefined) {
          const wasTemplate = i18n.historyDetailedChanges?.wasValue || 'was {{value}}';
          const wasText =
            typeof wasTemplate === 'string'
              ? wasTemplate.replace('{{value}}', oldValue)
              : `was ${oldValue}`;
          changeDescriptions.push(
            `${fieldName}: ${i18n.historyDetailedChanges?.removed || 'Removed'} (${wasText})`
          );
        } else {
          changeDescriptions.push(
            `${fieldName}: ${i18n.historyDetailedChanges.changedFrom} ${oldValue} ${i18n.historyDetailedChanges.to} ${newValue}`
          );
        }
      }
    }
  });

  return changeDescriptions.length > 0
    ? changeDescriptions.join('; ')
    : i18n.historyDetailedChanges.noChangesDetected;
}

// Memoized FiltersPanel
const FiltersPanel = memo(function FiltersPanel({
  showTitle = true,
  dateRange,
  setDateRange,
  userOptions,
  selectedUsers,
  handleUserToggle,
  entityInput,
  handleEntityInputChange,
  entityOptions,
  setEntityQuery,
  setEntityInput,
  actionTypeOptions,
  selectedActions,
  handleActionToggle,
  resetFilters,
  i18n,
}: {
  showTitle?: boolean;
  dateRange: any;
  setDateRange: any;
  userOptions: string[];
  selectedUsers: string[];
  handleUserToggle: (username: string) => void;
  entityInput: string;
  handleEntityInputChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  entityOptions: string[];
  setEntityQuery: (v: string) => void;
  setEntityInput: (v: string) => void;
  actionTypeOptions: { label: string; value: string }[];
  selectedActions: string[];
  handleActionToggle: (action: string) => void;
  resetFilters: () => void;
  i18n: any;
}) {
  const { actualTheme } = useTheme();

  return (
    <div className="space-y-6">
      {showTitle && (
        <h3 className="text-md mb-4 font-medium">{i18n.historyFilters?.title || 'Filters'}</h3>
      )}
      <div className="space-y-1">
        <Label className="block text-sm font-semibold uppercase">
          {i18n.historyFilters?.dateRange || 'Date Range'}
        </Label>
        <DatePickerWithRange date={dateRange} setDate={setDateRange} />
      </div>
      <div className="space-y-1">
        <Label className="block text-sm font-semibold uppercase">
          {i18n.historyFilters?.user || 'User'}
        </Label>
        <div className="max-h-40 space-y-2 overflow-y-auto rounded-md border p-3">
          {userOptions.length > 0 ? (
            userOptions.map(username => (
              <div key={username} className="flex items-center space-x-2 py-1">
                <Checkbox
                  id={`user-${username}`}
                  checked={selectedUsers.includes(username)}
                  onCheckedChange={() => handleUserToggle(username)}
                />
                <Label htmlFor={`user-${username}`} className="cursor-pointer text-sm font-normal">
                  {username}
                </Label>
              </div>
            ))
          ) : (
            <div className="py-2 text-sm text-gray-500">
              {i18n.historyFilters?.noUsersAvailable || 'No users available'}
            </div>
          )}
        </div>
      </div>
      <div className="space-y-1">
        <Label className="block text-sm font-semibold uppercase">
          {i18n.historyFilters?.entity || 'Entity'}
        </Label>
        <Input
          placeholder={i18n.historyFilters?.searchEntity || 'Search entity...'}
          onChange={handleEntityInputChange}
          value={entityInput}
          className="w-full"
        />
        {entityInput && entityOptions.length > 0 && (
          <div className="mt-1 max-h-32 overflow-y-auto rounded-md border p-2 text-sm">
            {entityOptions
              .filter(option => option.toLowerCase().includes(entityInput.toLowerCase()))
              .slice(0, 5)
              .map((option, index) => (
                <div
                  key={index}
                  className="cursor-pointer rounded p-1 hover:bg-gray-100"
                  onClick={() => {
                    setEntityQuery(option);
                    setEntityInput(option);
                  }}
                >
                  {option}
                </div>
              ))}
          </div>
        )}
      </div>
      <div className="space-y-1">
        <Label className="block text-sm font-semibold uppercase">
          {i18n.historyFilters?.action || 'Action'}
        </Label>
        <div className="space-y-2 rounded-md border p-3">
          {actionTypeOptions.map(action => (
            <div key={action.value} className="flex items-center space-x-2 py-1">
              <Checkbox
                id={`action-${action.value}`}
                checked={selectedActions.includes(action.value)}
                onCheckedChange={() => handleActionToggle(action.value)}
              />
              <Label
                htmlFor={`action-${action.value}`}
                className="cursor-pointer text-sm font-normal"
              >
                {action.label}
              </Label>
            </div>
          ))}
        </div>
      </div>
      <div className="flex space-x-2 pt-2">
        <Button
          variant="outline"
          onClick={resetFilters}
          className={
            actualTheme === 'dark'
              ? 'border-gray-600 bg-gray-800 text-gray-200 hover:bg-gray-700 hover:text-gray-100'
              : ''
          }
        >
          {i18n.historyFilters?.reset || 'Reset'}
        </Button>
      </div>
    </div>
  );
});

export function ChangeHistoryReport() {
  // Get translations and theme
  const i18n = useReportspageTranslations();
  const { actualTheme } = useTheme();
  const { t } = useTranslation();
  const { orgSettings } = useOrgSettings();
  const dateFormat = useMemo(
    () => (orgSettings?.date_format || 'MDY') as DateFormat,
    [orgSettings?.date_format]
  );

  const queryClient = useQueryClient();
  const [dateRange, setDateRange] = useState<DateRange>({
    from: undefined,
    to: undefined,
  });

  const [page, setPage] = useState(1);
  const [selectedUsers, setSelectedUsers] = useState<string[]>([]);
  const [entityQuery, setEntityQuery] = useState<string>('');
  const [selectedActions, setSelectedActions] = useState<string[]>([]);
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [entityInput, setEntityInput] = useState('');

  // Query key includes only backend-supported filters
  const queryKey = ['changeHistory', dateRange, selectedUsers];

  const { data, isLoading, error } = useQuery({
    queryKey,
    queryFn: () =>
      axiosInstance
        .get<ChangeHistoryItem[]>(
          paths.analytics.changeHistory({
            ...serializeDateRange(dateRange),
            users: selectedUsers,
          })
        )
        .then(res => res.data),
  });

  // User options from data
  const userOptions = data ? Array.from(new Set(data.map(item => item.username))).sort() : [];

  // Entity options from data
  const entityOptions = data
    ? Array.from(
        new Set(data.map(item => formatEntityType(item.entity_type, item.action, item.entity_id)))
      ).sort()
    : [];

  // Action counts for summary and checkboxes
  const getActionCounts = () => {
    if (!data || data.length === 0) return {};

    const actionCounts: Record<string, number> = {};
    data.forEach(item => {
      actionCounts[item.action] = (actionCounts[item.action] || 0) + 1;
    });
    return actionCounts;
  };
  const actionCounts = getActionCounts();
  const actionTypeOptions = useMemo(() => {
    // Get unique actions from data
    const dataActions = Array.from(new Set(data?.map(item => item.action) || []));

    // Define all possible actions with their translations
    const allActions = ['create', 'update', 'delete', 'archive'];

    // Create a Set to track unique actions and build options
    const uniqueActions = new Set<string>();
    const options: { label: string; value: string }[] = [];

    // Add actions from data first
    dataActions.forEach(action => {
      if (!uniqueActions.has(action)) {
        uniqueActions.add(action);
        options.push({
          label: action.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase()),
          value: action,
        });
      }
    });

    // Add any missing standard actions
    allActions.forEach(action => {
      if (!uniqueActions.has(action)) {
        uniqueActions.add(action);
        options.push({
          label:
            i18n.historyActionLabels?.[action as keyof typeof i18n.historyActionLabels] ||
            action.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase()),
          value: action,
        });
      }
    });

    return options;
  }, [data, i18n]);

  // Helper to format date and time
  const formatDateTime = (dateStr: string) => {
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return dateStr;
    return formatDateForDisplay(d, dateFormat);
  };

  // Color-coded action badge
  const getActionBadge = (action: string) => {
    return (
      <Badge className={getActionColor(action)}>
        {action === 'create' && (i18n.historyActions?.created || 'Created')}
        {action === 'update' && (i18n.historyActions?.updated || 'Updated')}
        {action === 'delete' && (i18n.historyActions?.deleted || 'Deleted')}
        {action === 'archive' && (i18n.historyActions?.archived || 'Archived')}
        {!(
          action === 'create' ||
          action === 'update' ||
          action === 'delete' ||
          action === 'archive'
        ) && action.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
      </Badge>
    );
  };

  // Handle user selection
  const handleUserToggle = (username: string) => {
    setSelectedUsers(current => {
      if (current.includes(username)) {
        return current.filter(u => u !== username);
      } else {
        return [...current, username];
      }
    });
  };

  // Handle action selection
  const handleActionToggle = (action: string) => {
    setSelectedActions(current => {
      if (current.includes(action)) {
        return current.filter(a => a !== action);
      } else {
        return [...current, action];
      }
    });
  };

  // Reset all filters and invalidate query
  const resetFilters = () => {
    setDateRange({ from: undefined, to: undefined });
    setSelectedUsers([]);
    setEntityQuery('');
    setSelectedActions([]);
    setPage(1);

    // Force a data refresh
    queryClient.invalidateQueries({ queryKey: ['changeHistory'] });
  };

  // Debounced update for entityQuery (used in query key)
  const debouncedSetEntityQuery = useCallback(
    debounce((value: string) => {
      setEntityQuery(value);
    }, 400),
    []
  );
  function handleEntityInputChange(e: React.ChangeEvent<HTMLInputElement>) {
    setEntityInput(e.target.value);
    debouncedSetEntityQuery(e.target.value);
  }
  // Keep entityInput in sync with entityQuery on clear
  useEffect(() => {
    if (!entityQuery) setEntityInput('');
  }, [entityQuery]);

  // Reset to page 1 when filters change
  useEffect(() => {
    setPage(1);
  }, [dateRange, selectedUsers, entityQuery, selectedActions]);

  // Pagination settings
  const itemsPerPage = 15;
  // Local filtering for action/entity
  let filteredData = data || [];
  if (selectedActions.length > 0) {
    filteredData = filteredData.filter(item => selectedActions.includes(item.action));
  }
  if (entityQuery) {
    filteredData = filteredData.filter(item =>
      formatEntityType(item.entity_type, item.action, item.entity_id)
        .toLowerCase()
        .includes(entityQuery.toLowerCase())
    );
  }
  const totalPages = filteredData.length ? Math.ceil(filteredData.length / itemsPerPage) : 0;
  const currentItems = filteredData
    ? filteredData.slice((page - 1) * itemsPerPage, page * itemsPerPage)
    : [];

  // Filter chips
  function FilterChips() {
    return (
      <div className="mb-4 flex flex-wrap gap-2">
        {dateRange.from && (
          <Badge className="flex items-center gap-1 bg-blue-500 text-white">
            {i18n.historyFilterChips.from || 'From:'} {format(dateRange.from, 'PP')}
            <button
              onClick={() => setDateRange({ ...dateRange, from: undefined })}
              className="ml-1 rounded-full p-0.5 hover:bg-gray-200"
            >
              <X className="size-3" />
            </button>
          </Badge>
        )}
        {dateRange.to && (
          <Badge className="flex items-center gap-1 bg-blue-500 text-white">
            {i18n.historyFilterChips.to || 'To:'} {format(dateRange.to, 'PP')}
            <button
              onClick={() => setDateRange({ ...dateRange, to: undefined })}
              className="ml-1 rounded-full p-0.5 hover:bg-gray-200"
            >
              <X className="size-3" />
            </button>
          </Badge>
        )}
        {selectedUsers.map(user => (
          <Badge key={user} className="flex items-center gap-1 bg-green-500 text-white">
            {i18n.historyFilterChips.user || 'User:'} {user}
            <button
              onClick={() => handleUserToggle(user)}
              className="ml-1 rounded-full p-0.5 hover:bg-gray-200"
            >
              <X className="size-3" />
            </button>
          </Badge>
        ))}
        {entityQuery && (
          <Badge className="flex items-center gap-1 bg-purple-500 text-white">
            {i18n.historyFilterChips.entity || 'Entity:'} {entityQuery}
            <button
              onClick={() => {
                setEntityQuery('');
                setEntityInput('');
              }}
              className="ml-1 rounded-full p-0.5 hover:bg-gray-200"
            >
              <X className="size-3" />
            </button>
          </Badge>
        )}
        {selectedActions.map(action => (
          <Badge key={action} className={`flex items-center gap-1 ${getActionColor(action)}`}>
            {i18n.historyFilterChips.action || 'Action:'}{' '}
            {action.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
            <button
              onClick={() => handleActionToggle(action)}
              className="ml-1 rounded-full p-0.5 text-inherit hover:bg-gray-200"
            >
              <X className="size-3" />
            </button>
          </Badge>
        ))}
        {(dateRange.from ||
          dateRange.to ||
          selectedUsers.length > 0 ||
          entityQuery ||
          selectedActions.length > 0) && (
          <Button
            variant="ghost"
            size="sm"
            onClick={resetFilters}
            className={
              actualTheme === 'dark'
                ? 'text-gray-300 hover:bg-gray-700 hover:text-gray-100'
                : 'text-gray-500'
            }
          >
            {i18n.historyFilters.clearAll || 'Clear all'}
          </Button>
        )}
      </div>
    );
  }

  // Action summary badges
  const ActionSummaryBadges = () => {
    if (Object.keys(actionCounts).length === 0) return null;

    // Helper function to get translated action label
    const getTranslatedActionLabel = (action: string): string => {
      const normalizedAction = action.toLowerCase().replace(/[^a-z]/g, '');
      const actionLabels = i18n.historyActionLabels || {};
      return (
        actionLabels[normalizedAction as keyof typeof actionLabels] ||
        action.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())
      );
    };

    return (
      <div className="mb-4 flex flex-wrap gap-2">
        {Object.entries(actionCounts).map(([action, count]) => (
          <Badge key={action} variant="outline" className="flex items-center gap-1">
            {getTranslatedActionLabel(action)}: {count}
          </Badge>
        ))}
      </div>
    );
  };

  /**
   * Resolve the `details` field coming from the backend. The backend can return:
   *   1) A plain string already translated
   *   2) A translation key (e.g. "productdetailpage.tabs.history.summaries.productUpdated")
   *   3) A JSON object with the shape: { translationKey: string, params?: Record<string, unknown> }
   *
   * This helper translates these different representations to a final, human-readable
   * string using react-i18next. It falls back to the raw value if no translation is found.
   */
  const resolveDetails = (details: unknown): string => {
    if (details == null) return '';

    // Helper to attempt to map raw English summary strings to translation keys
    const parseSummary = (
      text: string
    ): { key: string; params?: Record<string, unknown> } | null => {
      const patterns: Array<{
        regex: RegExp;
        key: string;
        transform?: (match: RegExpExecArray) => Record<string, unknown>;
      }> = [
        {
          regex: /^Product '(.+?)' was created$/i,
          key: 'productdetailpage.tabs.history.summaries.productCreated',
          transform: m => ({ name: m[1] }),
        },
        {
          regex: /^Product '(.+?)' was updated$/i,
          key: 'productdetailpage.tabs.history.summaries.productUpdated',
          transform: m => ({ name: m[1] }),
        },
        {
          regex: /^Product '(.+?)' was archived$/i,
          key: 'productdetailpage.tabs.history.summaries.productArchived',
          transform: m => ({ name: m[1] }),
        },
        {
          regex: /^Product '(.+?)' was permanently deleted$/i,
          key: 'productdetailpage.tabs.history.summaries.productDeleted',
          transform: m => ({ name: m[1] }),
        },
        {
          regex: /^Product '(.+?)' was permanently deleted in bulk$/i,
          key: 'productdetailpage.tabs.history.summaries.productBulkDeleted',
          transform: m => ({ name: m[1] }),
        },
        {
          regex: /^Product '(.+?)' was (.+?) in bulk$/i,
          key: 'productdetailpage.tabs.history.summaries.productBulkUpdated',
          transform: m => ({ name: m[1], summary: m[2] }),
        },
        {
          regex: /^Added (.+?) price of ([A-Z]{3}) ([0-9.,]+)/i,
          key: 'productdetailpage.tabs.history.summaries.priceAdded',
          transform: m => ({ priceType: m[1], currency: m[2], amount: m[3] }),
        },
        {
          regex: /^Updated (.+?) price to ([A-Z]{3}) ([0-9.,]+)/i,
          key: 'productdetailpage.tabs.history.summaries.priceUpdated',
          transform: m => ({ priceType: m[1], currency: m[2], amount: m[3] }),
        },
        {
          regex: /^Deleted (.+?) price of ([A-Z]{3}) ([0-9.,]+)/i,
          key: 'productdetailpage.tabs.history.summaries.priceDeleted',
          transform: m => ({ priceType: m[1], currency: m[2], amount: m[3] }),
        },
      ];

      for (const p of patterns) {
        const match = p.regex.exec(text);
        if (match) {
          return { key: p.key, params: p.transform ? p.transform(match) : undefined };
        }
      }
      return null;
    };

    // Case 3 – already an object with translationKey
    if (typeof details === 'object' && !Array.isArray(details)) {
      const maybeKey = (details as any).translationKey;
      if (typeof maybeKey === 'string') {
        return String(t(maybeKey, (details as any).params || {}));
      }
      // Fallback – stringify unknown object
      try {
        return JSON.stringify(details);
      } catch {
        return String(details);
      }
    }

    // Case 2 or Case 1 – a string
    if (typeof details === 'string') {
      // Attempt to parse JSON first
      try {
        const parsed = JSON.parse(details);
        if (parsed && typeof parsed === 'object' && 'translationKey' in parsed) {
          return String(t((parsed as any).translationKey, (parsed as any).params || {}));
        }
      } catch {
        /* ignore */
      }

      // Try translating the string directly
      const translatedDirect = t(details, { defaultValue: details });
      if (typeof translatedDirect === 'string' && translatedDirect !== details) {
        return translatedDirect;
      }

      // Attempt heuristic mapping from English summary to translation key
      const mapped = parseSummary(details);
      if (mapped) return String(t(mapped.key, mapped.params));

      return details;
    }

    // Any other type
    return String(details);
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <h2 className="text-lg font-medium">{i18n.getHistoryText('title') || 'Change History'}</h2>
        <div className="md:hidden">
          <Sheet open={isFilterOpen} onOpenChange={setIsFilterOpen}>
            <SheetTrigger asChild>
              <Button
                variant="outline"
                size="sm"
                className={`flex items-center gap-2 ${actualTheme === 'dark' ? 'border-gray-600 bg-gray-800 text-gray-200 hover:bg-gray-700 hover:text-gray-100' : ''}`}
              >
                <Filter className="size-4" /> {i18n.historyFilters?.title || 'Filters'}
              </Button>
            </SheetTrigger>
            <SheetOverlay />
            <SheetContent side="right" className="w-[300px] sm:w-[400px]">
              <div className="pt-6">
                <FiltersPanel
                  showTitle={true}
                  dateRange={dateRange}
                  setDateRange={setDateRange}
                  userOptions={userOptions}
                  selectedUsers={selectedUsers}
                  handleUserToggle={handleUserToggle}
                  entityInput={entityInput}
                  handleEntityInputChange={handleEntityInputChange}
                  entityOptions={entityOptions}
                  setEntityQuery={setEntityQuery}
                  setEntityInput={setEntityInput}
                  actionTypeOptions={actionTypeOptions}
                  selectedActions={selectedActions}
                  handleActionToggle={handleActionToggle}
                  resetFilters={resetFilters}
                  i18n={i18n}
                />
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>

      {/* Move ActionSummaryBadges above the flex row so chips span full width */}
      <ActionSummaryBadges />

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-4">
        {/* Main area */}
        <div className="lg:col-span-3">
          <FilterChips />

          {isLoading ? (
            <div className="space-y-2">
              <Skeleton className="h-[400px] w-full" />
            </div>
          ) : error ? (
            <div className="rounded border p-4 text-red-500">
              {i18n.errors.history.failedToLoad}
            </div>
          ) : (
            <Card>
              <CardContent className="p-4">
                <Table className="w-full table-fixed">
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-20">
                        {i18n.historyTable?.headers?.date || 'Date'}
                      </TableHead>
                      <TableHead className="w-24">
                        {i18n.historyTable?.headers?.user || 'User'}
                      </TableHead>
                      <TableHead className="w-28">
                        {i18n.historyTable?.headers?.entity || 'Entity'}
                      </TableHead>
                      <TableHead className="w-20">
                        {i18n.historyTable?.headers?.action || 'Action'}
                      </TableHead>
                      <TableHead className="w-44">
                        {i18n.historyTable?.headers?.details || 'Details'}
                      </TableHead>
                      <TableHead className="w-52">
                        {i18n.historyTable?.headers?.changes || 'Changes'}
                      </TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {currentItems.length > 0 ? (
                      currentItems.map(item => (
                        <TableRow key={item.id}>
                          <TableCell className="whitespace-nowrap text-xs">
                            {formatDateTime(item.date)}
                          </TableCell>
                          <TableCell className="truncate text-xs">{item.username}</TableCell>
                          <TableCell className="truncate text-xs">
                            {formatEntityType(item.entity_type, item.action, item.entity_id)}
                          </TableCell>
                          <TableCell>{getActionBadge(item.action)}</TableCell>
                          <TableCell className="text-xs">
                            <div className="truncate" title={String(resolveDetails(item.details))}>
                              {((): string => {
                                const full = String(resolveDetails(item.details));
                                return full.length > 50 ? full.substring(0, 50) + '...' : full;
                              })()}
                            </div>
                          </TableCell>
                          <TableCell className="text-xs">
                            <div
                              className={`truncate ${actualTheme === 'dark' ? 'text-gray-300' : 'text-gray-600'}`}
                              title={formatDetailedChanges(item.payload, i18n, t)}
                            >
                              {((): string => {
                                const fullText = formatDetailedChanges(item.payload, i18n, t);
                                return fullText.length > 50
                                  ? fullText.substring(0, 50) + '...'
                                  : fullText;
                              })()}
                            </div>
                          </TableCell>
                        </TableRow>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell colSpan={6} className="py-8 text-center text-gray-500">
                          {i18n.errors.history.noDataAvailable}
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>

                {totalPages > 1 && (
                  <div className="mt-4">
                    <Pagination>
                      <PaginationContent>
                        <PaginationItem>
                          <PaginationPrevious
                            onClick={() => setPage(p => Math.max(1, p - 1))}
                            className={
                              page === 1 ? 'pointer-events-none opacity-50' : 'cursor-pointer'
                            }
                          />
                        </PaginationItem>

                        {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                          // Show pages around current page
                          const pageNum =
                            page <= 3
                              ? i + 1
                              : page >= totalPages - 2
                                ? totalPages - 4 + i
                                : page - 2 + i;

                          if (pageNum > 0 && pageNum <= totalPages) {
                            return (
                              <PaginationItem key={pageNum}>
                                <PaginationLink
                                  onClick={() => setPage(pageNum)}
                                  isActive={page === pageNum}
                                >
                                  {pageNum}
                                </PaginationLink>
                              </PaginationItem>
                            );
                          }
                          return null;
                        })}

                        <PaginationItem>
                          <PaginationNext
                            onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                            className={
                              page === totalPages
                                ? 'pointer-events-none opacity-50'
                                : 'cursor-pointer'
                            }
                          />
                        </PaginationItem>
                      </PaginationContent>
                    </Pagination>
                  </div>
                )}
              </CardContent>
            </Card>
          )}
        </div>

        {/* Filters panel - desktop only */}
        <aside className="hidden lg:block">
          <Card className="sticky top-4">
            <CardContent className="p-4">
              <FiltersPanel
                showTitle={true}
                dateRange={dateRange}
                setDateRange={setDateRange}
                userOptions={userOptions}
                selectedUsers={selectedUsers}
                handleUserToggle={handleUserToggle}
                entityInput={entityInput}
                handleEntityInputChange={handleEntityInputChange}
                entityOptions={entityOptions}
                setEntityQuery={setEntityQuery}
                setEntityInput={setEntityInput}
                actionTypeOptions={actionTypeOptions}
                selectedActions={selectedActions}
                handleActionToggle={handleActionToggle}
                resetFilters={resetFilters}
                i18n={i18n}
              />
            </CardContent>
          </Card>
        </aside>
      </div>
    </div>
  );
}

export default ChangeHistoryReport;
